// src/components/congressional/MemberProfilesTab.jsx
// Congressional Trade Tracker — Tab 2 (Member Profiles).
// Extracted from CongressPage.jsx — see TradeFeedTab.jsx's header for the
// full split rationale.
//
// THIS PASS (owner request):
//   - Standard filter/pagination UX: ConFilterBar/ConPaginationFooter, same
//     components TradeFeedTab.jsx now uses, so a member sees one consistent
//     UX across every tab rather than each one having its own filter row.
//   - Active/Inactive/All status filter (default 'active') — congressMembers.js's
//     /members endpoint now supports this, querying CONGRESS_MEMBERS,
//     INACTIVE_CONGRESS_MEMBERS, or a UNION of both. Previously this list
//     only ever showed the 537 currently-active members with no way to see
//     anyone who'd left Congress.
//   - Window filter (default 'ytd') ADDED THIS PASS — reversing an earlier
//     judgment call that window didn't have a clean meaning on a member
//     list. FIXED mid-session (real design flaw, caught by the owner
//     directly): the first version used window to EXCLUDE any member with
//     zero trades — wrong, silently dropped ~447 of 537 active members down
//     to 90. Corrected design: window scopes a TRADECOUNT column via a
//     correlated subquery, and the list sorts by it descending — every
//     matching member still appears (0 shown honestly, not hidden), busiest
//     traders surface first.
//   - Each row now shows an Active/status badge (ISACTIVE/REASON returned by
//     the backend), same labeling convention as CongressMemberPage.jsx's own
//     header badge.
//
// EARLIER PASS (CONGRESSIONAL-PAGE-REDESIGN-DESIGN.md item 8), unchanged:
// the original build's inline right-panel detail is REMOVED, not just
// refactored — replaced by navigation to the Member Detail Subpage, which
// now owns that content plus the full trade history and photo the inline
// panel never had room for. A designed simplification, not a silent drop.

import { useState, useEffect, useCallback } from 'react';
import { Crown } from 'lucide-react';
import { API_URL } from '../../styles/shared';
import ConFilterBar, { ConPaginationFooter } from './ConFilterBar';
import { DEFAULT_PAGE_SIZE, PARTY_COLORS, Card, authHeaders } from './congressShared';

// Same REASON labels as CongressMemberPage.jsx's own — kept local rather
// than a third copy in congressShared.jsx, since only these two files need
// it; if a third consumer shows up, promote it then.
const REASON_LABELS = {
  RETIRED: 'Retired',
  LOST_ELECTION: 'Lost re-election',
  RESIGNED: 'Resigned',
  DECEASED: 'Deceased',
  DEPARTED_CONGRESS: 'No longer serving',
};

export default function MemberProfilesTab({ token, onNavigate }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // status default 'active' — unchanged behavior from before this pass
  // unless the member explicitly picks Inactive/All. window default 'ytd'
  // ADDED (owner correction — reversing an earlier judgment call not to
  // include it here): filters to members who traded within the window,
  // via congressMembers.js's own new EXISTS-subquery support.
  const [filters, setFilters] = useState({ chamber: '', party: '', search: '', status: 'active', window: 'ytd' });
  const [pendingFilters, setPendingFilters] = useState(filters);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    try {
      const r = await fetch(API_URL + '/api/congress/members?' + qs.toString(), { headers: authHeaders(token) });
      const d = await r.json().catch(() => ({ success: false }));
      if (!d.success) setError(d.message || 'Could not load member data.');
      else { setRows(d.data.rows || []); setTotal(d.data.total || 0); }
    } catch (e) { setError('Could not reach the server.'); }
    setLoading(false);
  }, [page, pageSize, filters, token]);

  useEffect(() => { loadList(); }, [loadList]);

  const applyFilters = () => { setPage(1); setFilters(pendingFilters); };
  const clearFilters = () => {
    const reset = { chamber: '', party: '', search: '', status: 'active', window: 'ytd' };
    setPendingFilters(reset); setFilters(reset); setPage(1);
  };
  const changePageSize = (e) => { setPage(1); setPageSize(parseInt(e.target.value, 10)); };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <ConFilterBar
        showChamber chamber={pendingFilters.chamber} onChamberChange={e => setPendingFilters(p => ({ ...p, chamber: e.target.value }))}
        showParty party={pendingFilters.party} onPartyChange={e => setPendingFilters(p => ({ ...p, party: e.target.value }))}
        showMemberName memberName={pendingFilters.search} onMemberNameChange={v => setPendingFilters(p => ({ ...p, search: v }))} memberNamePlaceholder="Search name"
        showStatus status={pendingFilters.status} onStatusChange={e => setPendingFilters(p => ({ ...p, status: e.target.value }))}
        showWindow dateWindow={pendingFilters.window} onWindowChange={e => setPendingFilters(p => ({ ...p, window: e.target.value }))}
        showPageSize pageSize={pageSize} onPageSizeChange={changePageSize}
        onApply={applyFilters} onClear={clearFilters} applyLabel="Search"
      />

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: '8px', padding: '0.75rem 1rem', color: '#ef4444', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</div>
      ) : (
        <>
          <p style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{total.toLocaleString()} members</p>
          {rows.map(row => (
            <Card
              key={`${row.BIOGUIDEID}:${row.CHAMBER}`}
              style={{ cursor: 'pointer' }}
              onClick={() => onNavigate('congressMember', { bioguideId: row.BIOGUIDEID, chamber: row.CHAMBER })}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ color: '#f1f5f9', fontWeight: 700 }}>{row.FIRSTNAME} {row.LASTNAME}</span>
                {row.ISCOMMITTEECHAIR === 1 && <Crown size={13} color="#ffd700" />}
                <span style={{ color: PARTY_COLORS[row.PARTY] || '#94a3b8', fontSize: '0.78rem', fontWeight: 700 }}>{row.PARTY}</span>
                {row.ISACTIVE === 1 ? (
                  <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 700, background: 'rgba(0,255,136,0.12)', color: '#00ff88' }}>Active</span>
                ) : (
                  <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 700, background: 'rgba(148,163,184,0.12)', color: '#94a3b8' }}>
                    {REASON_LABELS[row.REASON] || 'No longer serving'}
                  </span>
                )}
              </div>
              <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '0.25rem' }}>
                {row.CHAMBER} · {row.STATE}{row.DISTRICT ? `-${row.DISTRICT}` : ''} · {row.TENUREYEARS != null ? `${row.TENUREYEARS} yrs` : ''} {row.STATERANK ? `· ${row.STATERANK}` : ''}
                {row.ISACTIVE !== 1 && row.LASTSERVEDYEAR ? ` · last served ${row.LASTSERVEDYEAR}` : ''}
                {row.TRADECOUNT != null ? <span style={{ color: row.TRADECOUNT > 0 ? '#00ff88' : '#64748b' }}> · {row.TRADECOUNT} trade{row.TRADECOUNT !== 1 ? 's' : ''} this window</span> : null}
              </div>
            </Card>
          ))}
          <ConPaginationFooter
            page={page} totalPages={totalPages}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => Math.min(totalPages, p + 1))}
            showPageSize pageSize={pageSize} onPageSizeChange={changePageSize}
          />
        </>
      )}
    </div>
  );
}
